import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import maplibregl, { Map as MapLibreMap, LngLatBounds } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import clsx from "clsx";
import type {
  BlockStatus,
  FieldFeature,
  GeoJsonFeature,
  GeoJsonFeatureCollection,
  GeoJsonGeometry,
  Raster,
} from "@/lib/types";
import {
  BASEMAP_ATTRIBUTION,
  BASEMAP_TILE_URL,
  SATELLITE_ATTRIBUTION,
  SATELLITE_TILE_URLS,
} from "@/lib/config";
import { propertyLabel } from "@/lib/label";
import { editableLayerColor } from "@/lib/layerColors";
import { useLanguage } from "@/i18n";
import { useMapView, type MapView } from "@/lib/useMapView";
import MapLayersMenu from "./workspace/MapLayersMenu";
import type { FeatureQa } from "@/lib/api";
import { isRevisitActive } from "@/lib/revisit";

/** Imperative handle for "zoom to this imagery" -- the button lives in the
 *  sibling RasterPanel, not inside this component, so the parent page needs a
 *  ref to reach into a live map instance rather than a prop MapPreview could
 *  just watch (there's nothing to diff against for "the same raster, zoom to
 *  it again" on a second click). */
export type MapPreviewHandle = {
  flyToBounds: (bounds: [number, number, number, number]) => void;
  /** Frame one specific geometry -- the Field data table's per-row zoom.
   *
   *  Takes the geometry rather than a feature id on purpose: the table already
   *  holds the row it was clicked on, and passing the shape itself means this
   *  cannot go stale against MapPreview's own copy of the features array (the
   *  imperative handle closes over props, and the table's list refreshes on a
   *  poll). A degenerate geometry -- a single point, or a polygon whose corners
   *  coincide -- has zero extent and fitBounds would jump to maximum zoom, so
   *  those fall back to a centred flyTo instead. */
  zoomToGeometry: (geometry: GeoJsonGeometry) => void;
};

/** Boundary outlines to draw as reference context (block, ward, zone). */
export type BoundaryInput = {
  // A zone/ward/block primary key -- an int since the schema moved to IDENTITY.
  id: number;
  label: string;
  geometry: GeoJsonGeometry;
  kind: "block" | "ward" | "zone";
  /**
   * Survey progress, for the "Highlight progress" mode.
   *
   * A block's own status straight from the API. A ward has no status column
   * of its own, so the page derives one from the blocks inside it (see
   * ProjectWorkspacePage): in_progress if any block is, submitted only once
   * every block is. Zones carry none -- undefined simply means this outline
   * keeps its normal per-level colour when the mode is on.
   */
  status?: BlockStatus;
};

/* Colours mirror the mobile app's map palette so the same shape is the same
   colour in the field and in the office -- staff and surveyors describing a
   problem to each other are then talking about the same thing. */
const COLORS = {
  savedPoint: "#FFC107",
  savedPolygon: "#FF6D00",
  // Zone / ward / block each get their own hue, so all three can be on the
  // map at once and still be told apart. Block stays yellow -- it already
  // means "the actual survey unit" here and in the mobile app's own
  // MapColors.blockBoundary, and changing it would break that one piece of
  // shared vocabulary for no reason. Ward keeps the violet this file used to
  // draw every non-block level in (COLORS.reference); zone is new, since
  // "zone" was never a distinguishable layer before this.
  zone: "#48CAE4",
  ward: "#C084FC",
  block: "#FFD400",
  // An imported outline nobody has numbered yet. Deliberately NOT drawn from
  // the new blue/cyan palette above, even though "Vibrant Cyan" (#00B4D8)
  // fits it visually -- this specific color is a cross-repo invariant, not a
  // free choice: it has to equal the field app's MapColors.pendingPolygon
  // (lib/theme.dart), or a surveyor and an office staffer looking at the same
  // unsurveyed building see two different colors for it. Confirmed still
  // "#14B8A6" there; keep the two in sync if either ever changes.
  pending: "#14B8A6",
  // A property marked "revisit" from the Field data table: white, so it stands
  // out against both the orange of recorded work and the teal of pending.
  revisit: "#FFFFFF",
  // Overlap & sliver check results.
  qaOverlap: "#E11D48",
  qaSliver: "#D946EF",
};

/** Ward outlines are drawn much wider than block outlines so the two levels
 *  can be told apart at a glance, not just by colour. */
const BOUNDARY_WIDTH: Record<"zone" | "ward" | "block", number> = { zone: 1.6, ward: 3.6, block: 2.2 };
const BOUNDARY_WIDTH_HIGHLIGHT: Record<"zone" | "ward" | "block", number> = { zone: 1.6, ward: 4.6, block: 3.2 };

/**
 * Survey-progress colours for the "Highlight progress" mode.
 *
 * Deliberately outside COLORS above: those are per-LEVEL hues (which kind of
 * boundary this is), and these are per-STATE (how far along it is). Mixing
 * them into one object invites using a level colour where a state colour
 * belongs. Only in_progress and submitted get a colour -- pending keeps the
 * level's own, so switching the mode on highlights the wards and blocks that
 * have been started rather than repainting the whole map.
 */
const PROGRESS_COLORS: Partial<Record<BlockStatus, string>> = {
  in_progress: "#F97316",
  submitted: "#22C55E",
};

/** The three boundary levels this map can draw, each its own toggle and
 *  colour. Ordered outermost-first, which is also the draw order (so a block
 *  outline is never hidden under a zone one) and the order the dropdown lists
 *  them in. */
const BOUNDARY_KINDS = ["zone", "ward", "block"] as const;
type BoundaryKind = (typeof BOUNDARY_KINDS)[number];

const BASE_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: [BASEMAP_TILE_URL],
      tileSize: 256,
      attribution: BASEMAP_ATTRIBUTION,
    },
    satellite: {
      type: "raster",
      tiles: SATELLITE_TILE_URLS,
      tileSize: 256,
      maxzoom: 20,
      attribution: SATELLITE_ATTRIBUTION,
    },
  },
  // Satellite is part of the base style, so it always sits under the imagery
  // and every overlay added later. Hidden until the View panel switches it on.
  layers: [
    // What shows through when the base map is "none".
    { id: "background", type: "background", paint: { "background-color": "#EEF1F5" } },
    { id: "osm", type: "raster", source: "osm" },
    { id: "satellite", type: "raster", source: "satellite", layout: { visibility: "none" } },
  ],
};

const FEATURES_SRC = "collected-features";
const BOUNDARY_SRC = "reference-boundaries";
/** Every visible editable layer at once, told apart by a `lid` property and
 *  coloured by a data-driven `color` -- one source rather than one per layer,
 *  so switching a layer on or off is a setData call and not a style rebuild. */
const EDITABLE_SRC = "editable-layers";
/** Overlap areas and sliver polygons from the QA check. */
const QA_SRC = "qa-results";


/** A filter that matches no feature, for a layer that exists but has nothing
 *  selected yet. `["==", ["get", "id"], -1]` rather than `false`, because a
 *  literal boolean is not a valid MapLibre filter expression on its own and
 *  ids are always positive. */
const MATCH_NOTHING: maplibregl.FilterSpecification = ["==", ["get", "id"], -1];

/** Fill strength of field-data and editable-layer shapes; 0 under "Boundaries only". */
const FEATURE_FILL_OPACITY: maplibregl.DataDrivenPropertyValueSpecification<number> = [
  "case",
  ["==", ["get", "revisit"], true],
  0.6,
  0.28,
];
const EDITABLE_FILL_OPACITY = 0.18;

/**
 * Below this zoom, house-number labels are hidden.
 *
 * Not a style preference. A whole ward's worth of numbers at z13 is an
 * unreadable smear that also hides the shapes underneath, and since these are
 * DOM markers (see the labels effect) it is thousands of nodes for nothing. At
 * z17 a block fills the view and its numbers are legible and few.
 */
const LABEL_MIN_ZOOM = 17;
/** The scale bar's width in pixels (ScaleControl maxWidth below). */
const SCALE_BAR_PX = 90;
/**
 * Boundary names switch from wards to blocks once the scale bar reads 100 m,
 * i.e. once its full width covers less than this many metres (it shows the
 * largest round distance that fits, so 100 m until the width reaches 200 m).
 */
const BLOCK_LABEL_SCALE_M = 200;

/**
 * The attribute an editable layer's shapes are labelled by -- the property
 * number, and nothing else.
 *
 * Deliberately ONE name in several spellings, not a list of plausible columns.
 * An earlier version also accepted PLOT_NO, HOUSE_NO and the like, which meant
 * a layer got labelled by whatever a GIS export happened to call something
 * number-ish -- so the map showed a plot number, a survey number or nothing
 * depending on the file, and there was no way to ask for a layer NOT to be
 * labelled. The office names the column; the map draws that column and stays
 * quiet otherwise.
 *
 * The spellings all normalise to the same thing (lowercased, punctuation and
 * spaces dropped), so "Property No.", "PROPERTY_NO" and "propertyNo" are one
 * key:
 *
 * - `PROP_NO` -- what to use. Seven characters, so it survives a shapefile
 *   DBF's ten-character column limit intact.
 * - `PROPERTY_NO` / `Property No.` -- fine in a GeoJSON written directly.
 * - `PROPERTY_N` -- what PROPERTY_NO becomes after a round trip through a
 *   shapefile, which truncates at ten characters (the same truncation behind
 *   the Jalna layer's LAYOUT_NAM, CITY_SURVE and VILLAGE_NA). Accepted so that
 *   round trip does not silently switch the labels off.
 *
 * Order is priority. Kept in step with the field app's _propertyNumberKeys
 * (survey_map_screen.dart): changing one alone gives a layer two different
 * answers depending on which app is looking at it.
 */
const PROPERTY_NUMBER_KEYS = [
  "propertyno",
  "propertynumber",
  "propno",
  "propertyn",
];

/**
 * The attribute holding a shape's plot number, labelled by its own switch
 * ("Plot numbers on layers"), separately from the property number. Same
 * normalised spellings as the table's Plot no. column; `plotn` is PLOT_NO
 * names cut to ten characters by a shapefile round trip.
 */
const PLOT_NUMBER_KEYS = ["plotno", "plotnumber", "plotn"];

const normaliseAttributeKey = (key: string) =>
  key.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Which attribute holds these features' number, or null if none does. */
function propertyNumberKeyOf(
  features: { properties?: Record<string, unknown> | null }[],
  keys: string[] = PROPERTY_NUMBER_KEYS
): string | null {
  let best: string | null = null;
  let bestRank = keys.length;
  for (const f of features) {
    for (const key of Object.keys(f.properties ?? {})) {
      const rank = keys.indexOf(normaliseAttributeKey(key));
      if (rank === -1 || rank >= bestRank) continue;
      best = key;
      bestRank = rank;
      if (rank === 0) return best;
    }
    // One shape with attributes is enough to know the file's shape; a layer
    // whose first feature carries none falls through to the next.
    if (best !== null) return best;
  }
  return best;
}

/**
 * Writes an editable-layer label: the property number, and the plot number
 * (prefixed, e.g. "Plot 12", so the two are not confused) beside it, split
 * by a thin rule. `label` is remembered on the element so an unchanged one is
 * left alone.
 */
function fillLayerLabel(el: HTMLElement, label: string, property: string, plot: string, plotPrefix: string) {
  el.dataset.label = label;
  el.replaceChildren();
  if (property) {
    const part = document.createElement("span");
    part.textContent = property;
    el.appendChild(part);
  }
  if (plot) {
    const part = document.createElement("span");
    part.textContent = `${plotPrefix} ${plot}`;
    part.className = property ? "ml-1 border-l border-white/60 pl-1 font-medium opacity-90" : "";
    el.appendChild(part);
  }
}

/**
 * A point to hang a feature's label on.
 *
 * Polygons get the mean of their outer ring rather than a true centroid: for the
 * building-sized, roughly-convex footprints this survey collects the two are
 * within a few metres, and the average needs no geometry library. A concave
 * L-shape can place the label slightly off its own outline, which is acceptable
 * for a label but would not be for a hit test.
 */
function labelAnchor(geometry: GeoJSON.Geometry): [number, number] | null {
  if (geometry.type === "Point") {
    const c = geometry.coordinates as number[];
    return [c[0], c[1]];
  }
  const ring =
    geometry.type === "Polygon"
      ? (geometry.coordinates as number[][][])[0]
      : geometry.type === "MultiPolygon"
      ? (geometry.coordinates as number[][][][])[0]?.[0]
      : null;
  if (!ring || ring.length === 0) return null;
  let x = 0;
  let y = 0;
  for (const p of ring) {
    x += p[0];
    y += p[1];
  }
  return [x / ring.length, y / ring.length];
}

type LayerKey = "imagery" | "boundaries" | "features";

/**
 * One editable layer as the map draws it.
 *
 * Read-only here: the office sees these overlays alongside the survey data,
 * and a surveyor corrects them in the field app, standing at the thing being
 * mapped. See EditableLayersPanel for why that split exists.
 */
// ── camera motion ───────────────────────────────────────────────────────────
// One smooth curve for every programmatic move (filter changes, zoom-to from
// the table, zoom to imagery / layer), instant for reduced-motion users.
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
function cameraMotion(duration = 900): { duration: number; easing?: (t: number) => number; essential?: boolean } {
  const reduce =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  return reduce ? { duration: 0 } : { duration, easing: easeInOutCubic, essential: true };
}

export type DrawnEditableLayer = {
  id: number;
  name: string;
  collection: GeoJsonFeatureCollection;
};

const MapPreview = forwardRef<MapPreviewHandle, {
  rasters: Raster[];
  features?: FieldFeature[];
  boundaries?: BoundaryInput[];
  /** The field-data row last zoomed to from the table, outlined in white until
   *  another is picked. Null clears the highlight. */
  selectedFeatureId?: number | null;
  /** Uploaded overlays currently switched on, drawn above the survey data. */
  editableLayers?: DrawnEditableLayer[];
  /** The editable-layer shape last zoomed to from the Field data table,
   *  outlined in white like selectedFeatureId. `position` is the shape's place
   *  in its layer. */
  selectedLayerShape?: { layerId: number; position: number } | null;
  /** Overlap & sliver check: whether it is on, its toggle, and its result. The
   *  page runs the check (it knows the filter); the map draws it. Omit
   *  onQaToggle to hide the option. */
  qaEnabled?: boolean;
  qa?: FeatureQa | null;
  /** Layers, labels, base map and analysis modes -- owned by the page so the
   *  Filters & Layers panel can drive them (see lib/useMapView.ts). Without
   *  it the map keeps its own and shows a compact Layers menu instead. */
  view?: MapView;
}>(function MapPreview(
  {
    rasters,
    features = [],
    boundaries = [],
    selectedFeatureId = null,
    editableLayers = [],
    selectedLayerShape = null,
    qaEnabled = false,
    qa = null,
    view: viewProp,
  },
  ref
) {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [cursor, setCursor] = useState<{ lng: number; lat: number } | null>(null);
  /** Drives the "zoom in to see property numbers" hint. Null until the map loads. */
  const [zoom, setZoom] = useState<number | null>(null);
  const ownView = useMapView();
  const view = viewProp ?? ownView.view;
  const {
    visible,
    highlightProgress,
    basemap,
    boundaryLabels: showBoundaryLabels,
    editableLabels: showEditableLabels,
    editablePlotLabels: showEditablePlotLabels,
    outlineOnly,
  } = view;
  /** The Legend popover (bottom right). */
  const [legendOpen, setLegendOpen] = useState(false);
  /** Live house-number labels, keyed by feature id. See the labels effect. */
  const labelMarkersRef = useRef<Map<number, maplibregl.Marker>>(new Map());
  /** Boundary name labels, keyed "kind:id" -- ids are only unique per level,
   *  so a ward and a block can both be id 4. */
  const boundaryLabelsRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  /** Those labels' markers, keyed "lid:idx" -- plot numbers repeat across a
   *  city, so unlike the boundary names these cannot be keyed by their text. */
  const editableLabelsRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  /** Set once we've auto-framed, so later polling refreshes don't yank the
   *  camera back while someone is panning around. */
  const framedRef = useRef(false);
  /** A frame on the imagery or the data that the zones may still replace. */
  const provisionalFrameRef = useRef<"imagery" | "data" | null>(null);
  /** See the init effect below -- tracks a deferred teardown so React 18
   *  Strict Mode's dev-only double-invoke doesn't destroy and rebuild the
   *  whole map. */
  const pendingRemovalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * Hands data to the map once the camera is still. Re-sending thousands of
   * shapes in the middle of a fly-to is what made switching wards and blocks
   * stutter; now the flight runs on a quiet main thread and the shapes land
   * as it settles. Only the latest data per key is applied.
   */
  const pendingDataRef = useRef<Record<string, () => void>>({});
  const applyWhenSettled = (key: string, apply: () => void) => {
    const map = mapRef.current;
    if (!map) return;
    const waiting = key in pendingDataRef.current;
    pendingDataRef.current[key] = apply;
    if (waiting) return;
    // A frame later, so a fly-to started in the same commit is already moving.
    requestAnimationFrame(() => {
      const run = () => {
        const fn = pendingDataRef.current[key];
        delete pendingDataRef.current[key];
        fn?.();
      };
      if (mapRef.current?.isMoving()) mapRef.current.once("moveend", run);
      else run();
    });
  };

  /** Labels (DOM markers) step aside during a programmatic flight: moving
   *  hundreds of them every frame is the other half of the stutter. */
  const beginFlight = (map: MapLibreMap) => {
    rootRef.current?.classList.add("is-flying");
    map.once("moveend", () => rootRef.current?.classList.remove("is-flying"));
  };

  useImperativeHandle(ref, () => ({
    flyToBounds: (bounds) => {
      const map = mapRef.current;
      if (!map) return;
      const [minLng, minLat, maxLng, maxLat] = bounds;
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 48, maxZoom: 19, ...cameraMotion() }
      );
      beginFlight(map);
      // A deliberate zoom-to should stick -- otherwise the raster-sync effect
      // below would treat this as a stale camera and re-run the original
      // auto-frame on the next data refresh, undoing the very thing the
      // button just did.
      framedRef.current = true;
    },
    zoomToGeometry: (geometry) => {
      const map = mapRef.current;
      if (!map) return;
      const pts = coordsOf(geometry);
      if (pts.length === 0) return;

      const bounds = new LngLatBounds();
      for (const c of pts) bounds.extend(c as [number, number]);
      framedRef.current = true;

      // A building outline is metres across, so cap the zoom -- fitBounds on
      // its own would go to z22 and show a shape larger than the viewport with
      // no context around it. A zero-extent geometry (a point, or a polygon
      // collapsed to one location) can't be fitted at all: fitBounds treats it
      // as an empty box and snaps to max zoom.
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      if (sw.lng === ne.lng && sw.lat === ne.lat) {
        map.flyTo({ center: sw, zoom: 19, ...cameraMotion() });
        beginFlight(map);
        return;
      }
      map.fitBounds(bounds, { padding: 80, maxZoom: 20, ...cameraMotion() });
      beginFlight(map);
    },
  }));

  const readyRasters = useMemo(
    () => rasters.filter((r) => r.status === "ready" && r.tile_url),
    [rasters]
  );

  const featureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: features.map((f) => ({
        type: "Feature" as const,
        geometry: f.geometry as unknown as GeoJSON.Geometry,
        properties: {
          id: f.id,
          // Joined for display only ("12" + "B" -> "12B", "12" + "1-24" ->
          // "12/1-24"). Two rows in the same block can share a house number and
          // differ only by partition, so the number alone would label them
          // identically in the popup.
          property_no: propertyLabel(f.property_no, f.partition),
          created_by: f.created_by ?? "",
          kind: f.geometry?.type === "Point" ? "point" : "polygon",
          // An imported outline waiting for a surveyor, vs. one already
          // recorded. Drives the colour split below -- a numberless
          // block-scoped feature is only ever an unsurveyed import.
          state: (f.property_no ?? "").trim() === "" ? "pending" : "done",
          revisit: isRevisitActive(f.properties),
        },
      })),
    }),
    [features]
  );

  const boundaryCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: boundaries.map((b) => ({
        type: "Feature" as const,
        geometry: b.geometry as unknown as GeoJSON.Geometry,
        properties: { id: b.id, label: b.label, kind: b.kind, status: b.status ?? "" },
      })),
    }),
    [boundaries]
  );

  // ── init once ─────────────────────────────────────────────────────────────
  //
  // This is the actual cause of the reported flicker. Next's reactStrictMode
  // is on, and React 18's dev-only Strict Mode runs every effect through
  // mount -> cleanup -> mount again, synchronously, specifically to shake out
  // effects that don't clean up properly. The old cleanup called map.remove()
  // and reset mapRef.current to null, so the guard below ("if mapRef.current,
  // bail") did nothing -- the second mount saw a null ref and built a whole
  // second maplibregl.Map from scratch: new WebGL context, style reloaded,
  // every basemap and raster tile refetched. That is a real, visible
  // destroy-and-rebuild of the map on every load, not a perceptual glitch --
  // and it is exactly why the duplicate tile requests showed up in the network
  // log for this page.
  //
  // The fix defers the teardown instead of running it inline. If the effect
  // fires again before the timeout elapses -- which is what Strict Mode's
  // synchronous remount looks like -- the pending removal is cancelled and the
  // existing map is reused untouched. A genuine unmount (navigating away) has
  // no second mount to cancel it, so the timeout fires for real and the map is
  // disposed as before.
  useEffect(() => {
    if (!containerRef.current) return;

    if (pendingRemovalRef.current !== null) {
      clearTimeout(pendingRemovalRef.current);
      pendingRemovalRef.current = null;
    }

    if (mapRef.current) return; // live map already exists; reuse it

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASE_STYLE,
      center: [73.8567, 18.5204], // Pune, until real geometry frames the view
      zoom: 11,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ maxWidth: SCALE_BAR_PX, unit: "metric" }), "bottom-right");
    map.on("mousemove", (e) => setCursor({ lng: e.lngLat.lng, lat: e.lngLat.lat }));
    // moveend, not "zoom": one update per gesture rather than one per frame.
    // Only the label hint reads this, and that cannot change mid-gesture in a
    // way worth re-rendering for.
    setZoom(map.getZoom());
    map.on("moveend", () => setZoom(map.getZoom()));
    // A pan or zoom by the user (not by fitBounds) means the auto-frame must
    // not move the map again -- see "frame the data once" below.
    map.on("movestart", (e) => {
      if ((e as { originalEvent?: unknown }).originalEvent) framedRef.current = true;
    });

    map.on("load", () => {
      map.addSource(BOUNDARY_SRC, { type: "geojson", data: emptyFC() });
      map.addSource(FEATURES_SRC, { type: "geojson", data: emptyFC() });

      // Boundaries: outline only, deliberately no fill.
      //
      // A translucent fill used to sit under all three levels here (and, for
      // blocks specifically, under the imagery too). It bought nothing:
      // stacking zone-under-ward-under-block fills over an orthomosaic just
      // muddies the imagery underneath, which is the thing an outline is
      // supposed to help read, not compete with. Lines alone say exactly as
      // much -- "this is the boundary" -- with no cost to the imagery.
      //
      // Dashed for zone and ward (context, not directly actionable), solid for
      // block (the actual unit a surveyor works in) -- the same convention
      // this file used before splitting the levels apart, now applied per
      // level instead of to "block" vs. "everything else".
      for (const kind of BOUNDARY_KINDS) {
        const isBlock = kind === "block";
        map.addLayer({
          id: `boundary-line-${kind}`,
          type: "line",
          source: BOUNDARY_SRC,
          filter: ["==", ["get", "kind"], kind],
          paint: {
            "line-color": COLORS[kind],
            "line-width": BOUNDARY_WIDTH[kind],
            ...(isBlock ? {} : { "line-dasharray": [3, 2] }),
          },
        });
      }
      // Wards on top of blocks: a ward edge is also the edge of the blocks along
      // it, and drawn underneath, the thin block line hid most of the wider
      // ward line. On top, ward borders read as a clear thick outline.
      map.moveLayer("boundary-line-ward");

      // Collected field data on top of everything.
      map.addLayer({
        id: "feature-polygon-fill",
        type: "fill",
        source: FEATURES_SRC,
        filter: ["==", ["get", "kind"], "polygon"],
        paint: {
          "fill-color": [
            "case",
            ["==", ["get", "revisit"], true], COLORS.revisit,
            ["==", ["get", "state"], "pending"], COLORS.pending,
            COLORS.savedPolygon,
          ],
          "fill-opacity": FEATURE_FILL_OPACITY,
        },
      });
      map.addLayer({
        id: "feature-polygon-line",
        type: "line",
        source: FEATURES_SRC,
        filter: ["==", ["get", "kind"], "polygon"],
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "revisit"], true], COLORS.revisit,
            ["==", ["get", "state"], "pending"], COLORS.pending,
            COLORS.savedPolygon,
          ],
          "line-width": ["case", ["==", ["get", "revisit"], true], 2.6, 1.8],
        },
      });
      map.addLayer({
        id: "feature-point",
        type: "circle",
        source: FEATURES_SRC,
        filter: ["==", ["get", "kind"], "point"],
        paint: {
          "circle-radius": 5,
          "circle-color": [
            "case",
            ["==", ["get", "revisit"], true], COLORS.revisit,
            ["==", ["get", "state"], "pending"], COLORS.pending,
            COLORS.savedPoint,
          ],
          "circle-stroke-color": "#1A1205",
          "circle-stroke-width": 1.5,
        },
      });

      // The row the Field data table last zoomed to, held until another is
      // picked. Its own layers rather than a paint expression on the ones
      // above, because the highlight has to read against BOTH the orange of a
      // recorded shape and the teal of an unnumbered one, over imagery -- a
      // halo (a wide dark line under a thin white one) does that regardless of
      // what colour the shape underneath is. The filter starts matching
      // nothing and is narrowed to the selected id by the effect below.
      //
      // NOT interactive: it sits above the click targets, so it is declared
      // with no click handler and the layers underneath keep receiving events.
      map.addLayer({
        id: "feature-selected-halo",
        type: "line",
        source: FEATURES_SRC,
        filter: MATCH_NOTHING,
        paint: { "line-color": "#0B1220", "line-width": 6, "line-opacity": 0.55 },
      });
      map.addLayer({
        id: "feature-selected-line",
        type: "line",
        source: FEATURES_SRC,
        filter: MATCH_NOTHING,
        paint: { "line-color": "#FFFFFF", "line-width": 2.4 },
      });
      map.addLayer({
        id: "feature-selected-point",
        type: "circle",
        source: FEATURES_SRC,
        filter: MATCH_NOTHING,
        paint: {
          "circle-radius": 9,
          "circle-color": "rgba(0,0,0,0)",
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 2.4,
        },
      });

      // ── editable layers, above the survey data ─────────────────────────
      //
      // On top because they are opt-in: every one of these is off until
      // someone turns it on, so the layer just switched on is the thing being
      // looked at. Colour is data-driven off a `color` property rather than
      // one styled layer per uploaded file -- a project with six overlays
      // would otherwise add eighteen style layers and rebuild the style on
      // every toggle.
      map.addSource(EDITABLE_SRC, { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "editable-fill",
        type: "fill",
        source: EDITABLE_SRC,
        filter: ["==", ["get", "kind"], "polygon"],
        paint: { "fill-color": ["get", "color"], "fill-opacity": EDITABLE_FILL_OPACITY },
      });
      map.addLayer({
        id: "editable-line",
        type: "line",
        source: EDITABLE_SRC,
        filter: ["!=", ["get", "kind"], "point"],
        paint: {
          "line-color": ["get", "color"],
          "line-width": 1.8,
        },
      });
      map.addLayer({
        id: "editable-point",
        type: "circle",
        source: EDITABLE_SRC,
        filter: ["==", ["get", "kind"], "point"],
        paint: {
          "circle-radius": 4.5,
          "circle-color": ["get", "color"],
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 1.2,
        },
      });
      // The editable-layer shape last zoomed to from the table: the same
      // dark halo + white line as feature-selected-*, on the editable source,
      // narrowed to one (layer, position) by the effect below.
      map.addLayer({
        id: "editable-selected-halo",
        type: "line",
        source: EDITABLE_SRC,
        filter: MATCH_NOTHING,
        paint: { "line-color": "#0B1220", "line-width": 6, "line-opacity": 0.55 },
      });
      map.addLayer({
        id: "editable-selected-line",
        type: "line",
        source: EDITABLE_SRC,
        filter: MATCH_NOTHING,
        paint: { "line-color": "#FFFFFF", "line-width": 2.4 },
      });
      map.addLayer({
        id: "editable-selected-point",
        type: "circle",
        source: EDITABLE_SRC,
        filter: MATCH_NOTHING,
        paint: {
          "circle-radius": 9,
          "circle-color": "rgba(0,0,0,0)",
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 2.4,
        },
      });
      // ── overlap & sliver check, on top so a problem is never hidden ─────
      map.addSource(QA_SRC, { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "qa-overlap-fill",
        type: "fill",
        source: QA_SRC,
        filter: ["==", ["get", "qa"], "overlap"],
        layout: { visibility: "none" },
        paint: { "fill-color": COLORS.qaOverlap, "fill-opacity": 0.6 },
      });
      map.addLayer({
        id: "qa-overlap-line",
        type: "line",
        source: QA_SRC,
        filter: ["==", ["get", "qa"], "overlap"],
        layout: { visibility: "none" },
        paint: { "line-color": COLORS.qaOverlap, "line-width": 2 },
      });
      map.addLayer({
        id: "qa-sliver-line",
        type: "line",
        source: QA_SRC,
        filter: ["==", ["get", "qa"], "sliver"],
        layout: { visibility: "none" },
        paint: { "line-color": COLORS.qaSliver, "line-width": 3 },
      });

      // Identify-on-click, matching what a surveyor sees when tapping a shape.
      const popup = new maplibregl.Popup({ closeButton: false, offset: 10 });
      for (const id of ["feature-point", "feature-polygon-fill"]) {
        map.on("click", id, (e) => {
          const p = e.features?.[0]?.properties as Record<string, string> | undefined;
          if (!p) return;
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="font:12px Inter,system-ui;color:#0b0b0b">
                 <strong>${p.property_no ? escapeHtml(p.property_no) : "No number"}</strong><br/>
                 <span style="color:#898781">${p.created_by ? "by " + escapeHtml(p.created_by) : ""}</span>
               </div>`
            )
            .addTo(map);
        });
        map.on("mouseenter", id, () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", id, () => (map.getCanvas().style.cursor = ""));
      }

      setReady(true);
    });

    mapRef.current = map;
    return () => {
      // Deferred, and cancellable by the next effect run -- see the comment
      // above this effect for why.
      pendingRemovalRef.current = setTimeout(() => {
        map.remove();
        mapRef.current = null;
        framedRef.current = false;
        provisionalFrameRef.current = null;
        pendingRemovalRef.current = null;
      }, 0);
    };
  }, []);

  // ── raster layers ─────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    // Drop layers for rasters that are gone, add ones that are new. Inserted
    // beneath the boundary layers so imagery never covers the vectors.
    for (const layer of map.getStyle().layers ?? []) {
      if (layer.id.startsWith("raster-") && !readyRasters.some((r) => `raster-${r.id}` === layer.id)) {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
        if (map.getSource(layer.id)) map.removeSource(layer.id);
      }
    }
    for (const r of readyRasters) {
      const id = `raster-${r.id}`;
      if (map.getSource(id)) continue;
      map.addSource(id, { type: "raster", tiles: [r.tile_url as string], tileSize: 256 });
      // Inserted below the outermost (zone) boundary line, so imagery never
      // covers any of the three boundary layers -- "boundary-fill" doesn't
      // exist any more, but the reasoning is the same: imagery goes in first.
      map.addLayer(
        { id, type: "raster", source: id, paint: { "raster-opacity": 0.92 } },
        map.getLayer("boundary-line-zone") ? "boundary-line-zone" : undefined
      );
      map.setLayoutProperty(id, "visibility", visible.imagery ? "visible" : "none");
    }
  }, [readyRasters, ready, visible.imagery]);

  // ── vector data ───────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    applyWhenSettled("vector", () => {
      (map.getSource(FEATURES_SRC) as maplibregl.GeoJSONSource | undefined)?.setData(
        featureCollection as unknown as GeoJSON.FeatureCollection
      );
      (map.getSource(BOUNDARY_SRC) as maplibregl.GeoJSONSource | undefined)?.setData(
        boundaryCollection as unknown as GeoJSON.FeatureCollection
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureCollection, boundaryCollection, ready]);

  // ── selected feature highlight ────────────────────────────────────────────
  //
  // Driven by a filter rather than by pushing a second copy of the geometry
  // into its own source: the shape is already in FEATURES_SRC, and a duplicate
  // would drift out of sync the moment the features array refreshes under it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const filter: maplibregl.FilterSpecification =
      selectedFeatureId === null ? MATCH_NOTHING : ["==", ["get", "id"], selectedFeatureId];
    for (const id of ["feature-selected-halo", "feature-selected-line", "feature-selected-point"]) {
      if (!map.getLayer(id)) continue;
      // The point ring would otherwise also draw around a selected polygon's
      // label anchor, and the two line layers around a selected point's
      // (empty) outline -- so each is additionally constrained to its own
      // geometry kind.
      const kind = id.endsWith("point") ? "point" : "polygon";
      map.setFilter(
        id,
        selectedFeatureId === null
          ? MATCH_NOTHING
          : ["all", filter, ["==", ["get", "kind"], kind]]
      );
    }
  }, [selectedFeatureId, ready]);

  // ── selected editable-layer shape highlight ──────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    for (const id of ["editable-selected-halo", "editable-selected-line", "editable-selected-point"]) {
      if (!map.getLayer(id)) continue;
      map.setFilter(
        id,
        selectedLayerShape === null
          ? MATCH_NOTHING
          : [
              "all",
              ["==", ["get", "lid"], selectedLayerShape.layerId],
              ["==", ["get", "pos"], selectedLayerShape.position],
              id.endsWith("point")
                ? ["==", ["get", "kind"], "point"]
                : ["!=", ["get", "kind"], "point"],
            ]
      );
    }
  }, [selectedLayerShape, ready]);

  // ── editable layers ───────────────────────────────────────────────────────
  const editableCollection = useMemo(() => {
    const out: GeoJSON.Feature[] = [];
    for (const layer of editableLayers) {
      const color = editableLayerColor(layer.id);
      // Resolved once per LAYER, from the first shape that has one: a file's
      // columns are uniform, and re-scanning ~37 attributes on each of 13,600
      // features to reach the same answer is the difference between this being
      // free and it being the slowest thing on the page.
      const numberKey = propertyNumberKeyOf(layer.collection.features);
      const plotKey = propertyNumberKeyOf(layer.collection.features, PLOT_NUMBER_KEYS);
      layer.collection.features.forEach((f, idx) => {
        if (!f?.geometry?.type) return;
        const number = numberKey ? f.properties?.[numberKey] : undefined;
        const plot = plotKey ? f.properties?.[plotKey] : undefined;
        out.push({
          type: "Feature",
          geometry: f.geometry as unknown as GeoJSON.Geometry,
          properties: {
            lid: layer.id,
            idx,
            // The shape's place in its layer. A partial or area-filtered
            // collection carries it as the feature `id`; a whole layer is in
            // position order, so there it is the index.
            pos: (() => {
              const fid = (f as { id?: unknown }).id;
              return typeof fid === "number" ? fid : idx;
            })(),
            color,
            kind: kindOf(f.geometry.type),
            // Carried on the feature so the labels effect below needs no
            // second pass over the layers to find it again.
            number:
              number === null || number === undefined ? "" : String(number).trim(),
            plot: plot === null || plot === undefined ? "" : String(plot).trim(),
          },
        });
      });
    }
    return { type: "FeatureCollection" as const, features: out };
  }, [editableLayers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    applyWhenSettled("editable", () =>
      (map.getSource(EDITABLE_SRC) as maplibregl.GeoJSONSource | undefined)?.setData(
        editableCollection as GeoJSON.FeatureCollection
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableCollection, ready]);

  // ── frame the project on open ─────────────────────────────────────────────
  //
  // The full extent of the zone boundaries -- the whole project area -- when
  // the project has zones; else the drone imagery's extent; else whatever data
  // there is. Zones, imagery and data load separately, so a frame on the
  // imagery or the data is provisional: zones arriving later re-frame, unless
  // the user has already moved the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || framedRef.current) return;

    const extentOf = (geoms: GeoJsonGeometry[]) => {
      const b = new LngLatBounds();
      let any = false;
      for (const g of geoms) {
        for (const c of coordsOf(g)) {
          b.extend(c as [number, number]);
          any = true;
        }
      }
      return any ? b : null;
    };
    const zones = extentOf(boundaries.filter((b) => b.kind === "zone").map((b) => b.geometry));
    if (zones) {
      map.fitBounds(zones, { padding: 40, duration: 0, maxZoom: 18 });
      framedRef.current = true;
      return;
    }

    const imagery = new LngLatBounds();
    let hasImagery = false;
    for (const r of readyRasters) {
      if (
        r.bounds_min_lon === null || r.bounds_min_lat === null ||
        r.bounds_max_lon === null || r.bounds_max_lat === null
      ) continue;
      imagery.extend([r.bounds_min_lon, r.bounds_min_lat]);
      imagery.extend([r.bounds_max_lon, r.bounds_max_lat]);
      hasImagery = true;
    }
    if (hasImagery) {
      if (provisionalFrameRef.current !== "imagery") {
        map.fitBounds(imagery, { padding: 48, duration: 0, maxZoom: 18 });
        provisionalFrameRef.current = "imagery";
      }
      return;
    }
    if (provisionalFrameRef.current) return;
    const data = extentOf(
      [boundaryCollection, featureCollection].flatMap((fc) =>
        fc.features.map((f) => f.geometry as unknown as GeoJsonGeometry)
      )
    );
    if (!data) return;
    map.fitBounds(data, { padding: 48, duration: 0, maxZoom: 18 });
    provisionalFrameRef.current = "data";
  }, [readyRasters, boundaries, boundaryCollection, featureCollection, ready]);

  // ── progress highlight ────────────────────────────────────────────────────
  //
  // Recolours the ward and block outlines by survey state instead of by level.
  // A paint-property update rather than a second set of layers: the outlines
  // are already drawn, and swapping their colour expression avoids duplicating
  // every filter and width alongside them.
  //
  // Zones are left alone. They have no status of their own (see BoundaryInput)
  // and a zone spans wards at every stage, so colouring it by progress would
  // be averaging away the thing the mode exists to show.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    for (const kind of BOUNDARY_KINDS) {
      const layerId = `boundary-line-${kind}`;
      if (!map.getLayer(layerId)) continue;

      const plain = COLORS[kind];
      if (!highlightProgress || kind === "zone") {
        map.setPaintProperty(layerId, "line-color", plain);
        map.setPaintProperty(layerId, "line-width", BOUNDARY_WIDTH[kind]);
        continue;
      }

      // Anything without a recognised status keeps the level colour, so a
      // ward the page couldn't derive a state for stays visible rather than
      // dropping to some "unknown" shade indistinguishable from missing.
      map.setPaintProperty(layerId, "line-color", [
        "case",
        ["==", ["get", "status"], "in_progress"],
        PROGRESS_COLORS.in_progress!,
        ["==", ["get", "status"], "submitted"],
        PROGRESS_COLORS.submitted!,
        plain,
      ]);
      // Thicker while highlighting, so a started block reads at a zoom where
      // the whole ward is in view -- which is the zoom this mode is for.
      map.setPaintProperty(layerId, "line-width", [
        "case",
        ["!=", ["get", "status"], "pending"],
        BOUNDARY_WIDTH_HIGHLIGHT[kind],
        BOUNDARY_WIDTH[kind],
      ]);
    }
  }, [highlightProgress, ready]);

  // ── basemap: street, satellite or none ────────────────────────────────────
  // Only the chosen one is shown, so the hidden ones fetch no tiles.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getLayer("osm")) map.setLayoutProperty("osm", "visibility", basemap === "street" ? "visible" : "none");
    if (map.getLayer("satellite")) map.setLayoutProperty("satellite", "visibility", basemap === "satellite" ? "visible" : "none");
  }, [basemap, ready]);

  // ── refit when the map's box changes size (side panel, table drawer) ─────
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Once the box stops changing (the drawer's slide or drag has ended), not
    // on every frame of it: each resize is a full redraw of every layer,
    // which is what made the slide stutter. The map's own box clips it
    // meanwhile, so nothing visibly breaks during the slide.
    let timer = 0;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => mapRef.current?.resize(), 120);
    });
    ro.observe(el);
    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [])
  // ── overlap & sliver results ──────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const out: GeoJSON.Feature[] = [];
    if (qaEnabled && qa) {
      for (const o of qa.overlaps) {
        if (!o.geometry) continue;
        out.push({
          type: "Feature",
          geometry: o.geometry as unknown as GeoJSON.Geometry,
          properties: { qa: "overlap" },
        });
      }
      for (const sl of qa.slivers) {
        out.push({
          type: "Feature",
          geometry: sl.geometry as unknown as GeoJSON.Geometry,
          properties: { qa: "sliver" },
        });
      }
    }
    (map.getSource(QA_SRC) as maplibregl.GeoJSONSource | undefined)?.setData({
      type: "FeatureCollection",
      features: out,
    });
    for (const id of ["qa-overlap-fill", "qa-overlap-line", "qa-sliver-line"]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", qaEnabled ? "visible" : "none");
    }
  }, [qa, qaEnabled, ready]);

  // ── zone / ward / block name labels ───────────────────────────────────────
  //
  // Same DOM-marker approach as the house numbers below, and for the same
  // reason (no glyphs in BASE_STYLE). Culled to the viewport. Ward names show
  // when zoomed out and give way to block names once the scale bar reads
  // 100 m (BLOCK_LABEL_SCALE_M); zone
  // names, when switched on, show at any zoom.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const markers = boundaryLabelsRef.current;
    const clearAll = () => {
      for (const marker of markers.values()) marker.remove();
      markers.clear();
    };

    const sync = () => {
      if (!BOUNDARY_KINDS.some((k) => showBoundaryLabels[k])) {
        clearAll();
        return;
      }

      const bounds = map.getBounds();
      const wanted = new Set<string>();
      // Zoomed out, ward names; zoomed in close, block names instead -- both
      // at once would bury each other.
      // Ground metres per screen pixel at the map's centre (512-px tiles).
      const metresPerPx =
        (40075016.686 * Math.cos((map.getCenter().lat * Math.PI) / 180)) /
        (512 * 2 ** map.getZoom());
      const closeIn = metresPerPx * SCALE_BAR_PX < BLOCK_LABEL_SCALE_M;

      for (const b of boundaries) {
        // A level switched off in the Boundaries group takes its labels with
        // it -- a name floating over no outline reads as a stray marker.
        if (!visible[b.kind] || !showBoundaryLabels[b.kind]) continue;
        if (b.kind === "ward" && closeIn) continue;
        if (b.kind === "block" && !closeIn) continue;
        const label = (b.label ?? "").trim();
        if (!label) continue;
        const anchorPt = labelAnchor(b.geometry as unknown as GeoJSON.Geometry);
        if (!anchorPt || !bounds.contains(anchorPt)) continue;

        const key = `${b.kind}:${b.id}`;
        wanted.add(key);

        const existing = markers.get(key);
        if (existing) {
          existing.setLngLat(anchorPt);
          const el = existing.getElement();
          if (el.textContent !== label) el.textContent = label;
          continue;
        }

        const el = document.createElement("div");
        el.textContent = label;
        el.className =
          "pointer-events-none select-none whitespace-nowrap rounded px-1 " +
          "text-[10px] font-semibold uppercase leading-4 tracking-wide " +
          "shadow-[0_0_0_1px_rgba(0,0,0,0.35)]";
        // Tinted to its own level, so a name is attributable to an outline
        // without tracing it -- three levels of plain white labels over each
        // other is the failure mode this avoids.
        el.style.color = "#FFFFFF";
        el.style.backgroundColor =
          b.kind === "zone" ? "#0E7490cc" : b.kind === "ward" ? "#7C3AEDcc" : "#A16207cc";

        markers.set(
          key,
          new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(anchorPt).addTo(map)
        );
      }

      for (const [key, marker] of markers) {
        if (!wanted.has(key)) {
          marker.remove();
          markers.delete(key);
        }
      }
    };

    sync();
    map.on("moveend", sync);
    return () => {
      map.off("moveend", sync);
      clearAll();
    };
  }, [boundaries, showBoundaryLabels, visible, ready]);

  // ── house-number labels ───────────────────────────────────────────────────
  //
  // DOM markers, not a symbol layer with text-field. BASE_STYLE is a raster
  // basemap with no `glyphs` URL, and MapLibre cannot render text without one:
  // a symbol layer would draw nothing at all, silently. Adding a glyph endpoint
  // would mean depending on a third-party font server (MapTiler et al. need an
  // API key) for what is a handful of short strings.
  //
  // The cost of markers is a DOM node each, so two things bound the count:
  // LABEL_MIN_ZOOM, and culling to the current viewport. Both are re-evaluated
  // on moveend, which is also when either could change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const markers = labelMarkersRef.current;

    const clearAll = () => {
      for (const marker of markers.values()) marker.remove();
      markers.clear();
    };

    const sync = () => {
      // "features" off hides the shapes, so their labels go too -- a floating
      // number with nothing under it reads as a bug.
      if (!visible.labels || !visible.features || map.getZoom() < LABEL_MIN_ZOOM) {
        clearAll();
        return;
      }

      const bounds = map.getBounds();
      const wanted = new Set<number>();

      for (const f of featureCollection.features) {
        const label = String(f.properties.property_no ?? "").trim();
        if (!label) continue;
        const anchor = labelAnchor(f.geometry);
        if (!anchor || !bounds.contains(anchor)) continue;

        const id = f.properties.id as number;
        wanted.add(id);
        const existing = markers.get(id);
        if (existing) {
          // Geometry can move under us (an edit elsewhere, a poll refresh).
          existing.setLngLat(anchor);
          const el = existing.getElement();
          if (el.textContent !== label) el.textContent = label;
          continue;
        }

        const el = document.createElement("div");
        el.textContent = label;
        el.className =
          "pointer-events-none select-none whitespace-nowrap rounded px-1 " +
          "text-[10px] font-semibold leading-4 text-white " +
          "bg-black/55 shadow-[0_0_0_1px_rgba(0,0,0,0.35)]";
        markers.set(
          id,
          new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat(anchor)
            .addTo(map)
        );
      }

      // Drop labels for features that scrolled out of view or disappeared.
      for (const [id, marker] of markers) {
        if (!wanted.has(id)) {
          marker.remove();
          markers.delete(id);
        }
      }
    };

    sync();
    map.on("moveend", sync);
    return () => {
      map.off("moveend", sync);
      clearAll();
    };
  }, [featureCollection, visible.labels, visible.features, ready]);

  // ── editable-layer property numbers ───────────────────────────────────────
  //
  // Same DOM-marker mechanism as the house numbers above, and bounded the same
  // two ways -- LABEL_MIN_ZOOM and viewport culling -- for a sharper reason: a
  // municipal plots layer is ~13,600 shapes, and one DOM node each with no
  // bound would lock the tab rather than merely slow it. A hard ceiling sits
  // on top of both, since a dense layout can genuinely fit several hundred
  // plots on screen at once.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const markers = editableLabelsRef.current;
    const MAX_ON_SCREEN = 300;

    const clearAll = () => {
      for (const marker of markers.values()) marker.remove();
      markers.clear();
    };

    const sync = () => {
      if ((!showEditableLabels && !showEditablePlotLabels) || map.getZoom() < LABEL_MIN_ZOOM) {
        clearAll();
        return;
      }

      const bounds = map.getBounds();
      const wanted = new Set<string>();

      for (const f of editableCollection.features) {
        // Property number and plot number, each by its own switch. With
        // both on, the plot number sits beside the property number.
        const property = showEditableLabels ? String(f.properties?.number ?? "") : "";
        const plot = showEditablePlotLabels ? String(f.properties?.plot ?? "") : "";
        if (!property && !plot) continue;
        const label = `${property}\n${plot}`;
        const anchor = labelAnchor(f.geometry);
        if (!anchor || !bounds.contains(anchor)) continue;

        const key = `${f.properties!.lid}:${f.properties!.idx}`;
        wanted.add(key);
        const existing = markers.get(key);
        if (existing) {
          existing.setLngLat(anchor);
          const el = existing.getElement();
          if (el.dataset.label !== label) fillLayerLabel(el, label, property, plot, t.mapPreview.plotPrefix);
        } else {
          const el = document.createElement("div");
          fillLayerLabel(el, label, property, plot, t.mapPreview.plotPrefix);
          // The layer's own colour, so a plot number is not read as one of the
          // survey's house numbers -- those are white on black above.
          el.className =
            "pointer-events-none select-none whitespace-nowrap rounded px-1 " +
            "text-[10px] font-semibold leading-4 text-white " +
            "shadow-[0_0_0_1px_rgba(0,0,0,0.35)]";
          el.style.backgroundColor = String(f.properties?.color ?? "#000000") + "d9";
          markers.set(
            key,
            new maplibregl.Marker({ element: el, anchor: "center" })
              .setLngLat(anchor)
              .addTo(map)
          );
        }

        if (wanted.size >= MAX_ON_SCREEN) break;
      }

      for (const [key, marker] of markers) {
        if (!wanted.has(key)) {
          marker.remove();
          markers.delete(key);
        }
      }
    };

    sync();
    map.on("moveend", sync);
    return () => {
      map.off("moveend", sync);
      clearAll();
    };
  }, [editableCollection, showEditableLabels, showEditablePlotLabels, ready, t]);

  // ── layer visibility ──────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const groups: Record<string, string[]> = {
      imagery: readyRasters.map((r) => `raster-${r.id}`),
      zone: [`boundary-line-zone`],
      ward: [`boundary-line-ward`],
      block: [`boundary-line-block`],
      features: ["feature-polygon-fill", "feature-polygon-line", "feature-point"],
    };
    for (const [key, ids] of Object.entries(groups)) {
      for (const id of ids) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", visible[key] ? "visible" : "none");
        }
      }
    }
  }, [visible, ready, readyRasters]);

  // "Boundaries only": the fill goes fully transparent, leaving the outline.
  // Opacity rather than hiding the fill layer, so clicking inside a shape
  // still opens its popup.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getLayer("feature-polygon-fill")) {
      map.setPaintProperty("feature-polygon-fill", "fill-opacity", outlineOnly.features ? 0 : FEATURE_FILL_OPACITY);
    }
    if (map.getLayer("editable-fill")) {
      map.setPaintProperty("editable-fill", "fill-opacity", outlineOnly.editable ? 0 : EDITABLE_FILL_OPACITY);
    }
  }, [outlineOnly.features, outlineOnly.editable, ready]);

  /** How many of each boundary level are actually on the map right now, for
   *  the dropdown's per-row counts and the header's combined one. */
  const boundaryCounts = useMemo(() => {
    const counts: Record<BoundaryKind, number> = { zone: 0, ward: 0, block: 0 };
    for (const b of boundaries) counts[b.kind] += 1;
    return counts;
  }, [boundaries]);

  /**
   * How many BLOCKS are in each state, for the highlight key.
   *
   * Blocks only, not wards: a ward's state here is derived from its blocks
   * (see BoundaryInput.status), so counting both would report the same work
   * twice at two different granularities and never add up to the block total
   * the rest of the portal quotes.
   */
  const progressCounts = useMemo(() => {
    const counts: Record<BlockStatus, number> = { pending: 0, in_progress: 0, submitted: 0 };
    for (const b of boundaries) {
      if (b.kind !== "block") continue;
      const status = b.status ?? "pending";
      if (status in counts) counts[status] += 1;
    }
    return counts;
  }, [boundaries]);

  const revisitCount = useMemo(
    () => features.filter((f) => isRevisitActive(f.properties)).length,
    [features]
  );

  const boundaryNames: Record<BoundaryKind, string> = {
    zone: t.mapPreview.boundaryZones,
    ward: t.mapPreview.boundaryWards,
    block: t.mapPreview.boundaryBlocks,
  };

  // What the Legend lists: only what is on the map right now.
  const legendRows: { color: string; label: string; count?: number; ring?: boolean; line?: boolean }[] = [];
  if (visible.features) {
    legendRows.push({ color: COLORS.savedPolygon, label: t.mapPreview.fieldData, count: features.length });
    if (revisitCount > 0) legendRows.push({ color: "#FFFFFF", label: t.mapPreview.markedForRevisit, count: revisitCount, ring: true });
  }
  if (highlightProgress) {
    legendRows.push({ color: PROGRESS_COLORS.in_progress!, label: t.mapPreview.inProgress, count: progressCounts.in_progress });
    legendRows.push({ color: PROGRESS_COLORS.submitted!, label: t.mapPreview.submitted, count: progressCounts.submitted });
    legendRows.push({ color: COLORS.block, label: t.mapPreview.notStarted, count: progressCounts.pending });
  }
  for (const k of BOUNDARY_KINDS) {
    if (visible[k] && boundaryCounts[k] > 0 && !(highlightProgress && k !== "zone")) {
      legendRows.push({ color: COLORS[k], label: boundaryNames[k], count: boundaryCounts[k], line: true });
    }
  }
  for (const l of editableLayers) {
    legendRows.push({ color: editableLayerColor(l.id), label: l.name, count: l.collection.features.length });
  }
  if (qaEnabled && qa) {
    legendRows.push({ color: COLORS.qaOverlap, label: t.mapPreview.qaOverlaps, count: qa.overlap_count });
    legendRows.push({ color: COLORS.qaSliver, label: t.mapPreview.qaSlivers, count: qa.sliver_count, line: true });
  }

  return (
    <div ref={rootRef} className="map-root relative h-full w-full overflow-hidden bg-surface">
      <div ref={containerRef} className="h-full w-full" />

      {!viewProp && (
        <div className="absolute left-2.5 top-2.5">
          <MapLayersMenu state={ownView} />
        </div>
      )}

      {/* Legend: what each colour on the map means, for what is shown now. The
          layer switches themselves live in the Filters & Layers panel. */}
      {/* Under the zoom buttons (top right), clear of the scale bar and
          attribution in the bottom corner. */}
      <div className="absolute right-2.5 top-[4.75rem] flex flex-col-reverse items-end gap-1.5">
        {legendOpen && legendRows.length > 0 && (
          <div className="thin-scroll max-h-72 w-60 overflow-y-auto rounded-md bg-surface-raised/95 p-2.5 shadow-menu backdrop-blur">
            <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-muted">
              {t.mapPreview.legend}
            </p>
            <ul className="space-y-1">
              {legendRows.map((row, i) => (
                <li key={`${row.label}-${i}`} className="flex items-center gap-2">
                  <span
                    className={clsx("flex-none", row.line ? "h-0.5 w-3.5 rounded-full" : "h-2.5 w-2.5 rounded-sm")}
                    style={{
                      backgroundColor: row.color,
                      boxShadow: row.ring ? "0 0 0 1px rgba(0,0,0,0.35)" : undefined,
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-ink-secondary" title={row.label}>
                    {row.label}
                  </span>
                  {row.count !== undefined && (
                    <span className="tabular flex-none text-2xs text-ink-muted">{row.count.toLocaleString("en-IN")}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={() => setLegendOpen((o) => !o)}
          aria-expanded={legendOpen}
          className={clsx(
            "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide shadow-menu transition",
            legendOpen ? "bg-accent-solid text-white" : "bg-surface-raised text-ink hover:bg-surface-sunken"
          )}
        >
          {t.mapPreview.legend}
        </button>
      </div>

      {/* Without this, switching Property numbers on while zoomed out looks like a
          broken toggle: it reports a count, and nothing appears. Says which of
          the two it is rather than leaving it to be guessed. */}
      {visible.labels && visible.features && zoom !== null && zoom < LABEL_MIN_ZOOM &&
        features.some((f) => (f.property_no ?? "").trim() !== "") && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-ink/80 px-2 py-0.5 text-2xs text-white">
            {t.mapPreview.zoomInForNumbers}
          </div>
        )}

      <div className="pointer-events-none absolute bottom-2 left-2.5 rounded bg-ink/80 px-2 py-0.5 font-mono text-2xs text-white">
        {cursor ? `${cursor.lat.toFixed(5)}, ${cursor.lng.toFixed(5)}` : "—"}
      </div>
    </div>
  );
});


export default MapPreview;

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

/** Collapses a GeoJSON type to the three things the style layers filter on.
 *  Multi* draws exactly like its single equivalent. */
function kindOf(type: string): "polygon" | "line" | "point" {
  if (type === "Polygon" || type === "MultiPolygon") return "polygon";
  if (type === "Point" || type === "MultiPoint") return "point";
  return "line";
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string)
  );
}

/** Flattens any GeoJSON geometry to a flat list of [lng, lat] pairs. */
function coordsOf(geom: GeoJsonGeometry | null | undefined): number[][] {
  if (!geom?.coordinates) return [];
  const out: number[][] = [];
  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      if (typeof node[0] === "number" && typeof node[1] === "number") {
        out.push([node[0], node[1]]);
      } else {
        node.forEach(walk);
      }
    }
  };
  walk(geom.coordinates);
  return out;
}

/** The maps colours, for the Filters & Layers panels swatches. */
export { COLORS as MAP_COLORS, PROGRESS_COLORS, BOUNDARY_KINDS };
